import { RemoteDataSource, Settings } from '@/datasources/remote_datasource';
import { AppConfig } from '@/datasources/AppConfig';
import { Failure } from '@/usecases/repository';
import { encryptToken, decryptToken, encryptId, decryptId } from '@/utils/tokenCrypto';

interface OuvidoriaCreationData {
  nome: string;
  cnpj?: string;
  razaoSocial?: string;
  naturezaJuridica?: string;
  nomeFantasia?: string;
  email?: string;
  telefone?: string;
  cidade?: string; // Nome do município (obrigatório segundo erro da API)
  geral?: boolean;
  etiquetas?: string[];
  logoFile?: File; // Required for ouvidoria-geral endpoint
  endereco?: {
    logradouro: string;
    numero: string;
    complemento?: string;
    bairro: string;
    cidade: string;
    estado: string;
    cep: string;
    pais: string;
    ibge?: string;
    latitude?: number;
    longitude?: number;
  };
  [key: string]: unknown;
}

interface UserCreationData {
  id: string; // email or phone
  nome: string;
}

interface OuvidoriaCreationResponse {
  id: string;
  nome: string;
  cnpj: string;
  agentes?: Array<{
    id: string;
    nome: string;
  }>;
}

interface UserCreationResponse {
  tokenAcesso: string;
  tokenAtualizacao: string;
  expira: string;
  recuperarConta?: {
    chave: string;
    segredo: string;
  };
}

class OuvidoriaCreationService {
  private readonly urbNetDataSource: RemoteDataSource;
  private readonly authDataSource: RemoteDataSource;

  constructor() {
    this.urbNetDataSource = new RemoteDataSource(Settings.urlBase);
    this.authDataSource = new RemoteDataSource(Settings.urlSecondary);
  }

  /**
   * Creates a new ouvidoria geral (general ouvidoria) with logo upload
   */
  async criarOuvidoriaGeral(
    municipioId: string,
    data: OuvidoriaCreationData
  ): Promise<{ success: OuvidoriaCreationResponse } | { failure: Failure }> {
    // Convert data to FormData as required by the API
    const formData = new FormData();
    
    // Required fields according to AdicionarOuvidoriaDados schema
    formData.append('nome', data.nome);
    formData.append('cnpj', data.cnpj || '');
    // Use geocoded coordinates if available, otherwise use defaults
    formData.append('latitude', String(data.endereco?.latitude || 0));
    formData.append('longitude', String(data.endereco?.longitude || 0));
    formData.append('altitude', '0'); // Default value
    formData.append('geral', 'true'); // Always true for ouvidoria geral
    
    // Optional fields
    if (data.razaoSocial) formData.append('razaoSocial', data.razaoSocial);
    if (data.naturezaJuridica) formData.append('naturezaJuridica', data.naturezaJuridica);
    if (data.nomeFantasia) formData.append('nomeFantasia', data.nomeFantasia);
    // Email and telefone should be sent even if null/empty (nullable fields)
    formData.append('email', data.email || '');
    formData.append('telefone', data.telefone || '');
    
    // Etiquetas - enviar cada etiqueta individualmente como valores separados
    if (data.etiquetas && Array.isArray(data.etiquetas) && data.etiquetas.length > 0) {
      data.etiquetas.forEach((etiqueta) => {
        formData.append('etiquetas', etiqueta);
      });
    }
    
    // enderecoStr - deve ser enviado como objeto JSON com os campos do endereço
    if (data.endereco) {
      const enderecoObj = {
        logradouro: data.endereco.logradouro,
        numero: data.endereco.numero,
        bairro: data.endereco.bairro,
        cidade: data.endereco.cidade,
        estado: data.endereco.estado,
        cep: data.endereco.cep,
        pais: data.endereco.pais,
        ibge: data.endereco.ibge || '',
      };
      formData.append('enderecoStr', JSON.stringify(enderecoObj));
    }

    // Logo file - optional for ouvidoria-geral endpoint
    if (data.logoFile) {
      formData.append('arquivos[0]', data.logoFile);
    }

    const response = await this.urbNetDataSource.post<OuvidoriaCreationResponse>({
      endpoint: `v1/municipios/${municipioId}/ouvidorias/ouvidoria-geral`,
      body: formData,
      requiredToken: false,
      headers: { 'accept': 'application/vnd.data.full.v1+json' },
    });

    if ('failure' in response) {
      return response;
    }

    return { success: response.success.body as OuvidoriaCreationResponse };
  }

  /**
   * Creates a new ouvidoria and extracts the IDs from the response
   */
  async criarOuvidoria(
    municipioId: string,
    data: OuvidoriaCreationData
  ): Promise<{ success: OuvidoriaCreationResponse } | { failure: Failure }> {
    // Convert data to FormData as required by the API
    const formData = new FormData();
    
    // Required fields according to AdicionarOuvidoriaDados schema
    formData.append('nome', data.nome);
    formData.append('cnpj', data.cnpj || '');
    // Use geocoded coordinates if available, otherwise use defaults
    formData.append('latitude', String(data.endereco?.latitude || 0));
    formData.append('longitude', String(data.endereco?.longitude || 0));
    formData.append('altitude', '0'); // Default value
    formData.append('geral', String(data.geral ?? true)); // Use geral from data or default to true
    
    // Optional fields
    if (data.razaoSocial) formData.append('razaoSocial', data.razaoSocial);
    if (data.naturezaJuridica) formData.append('naturezaJuridica', data.naturezaJuridica);
    if (data.nomeFantasia) formData.append('nomeFantasia', data.nomeFantasia);
    // Email and telefone should be sent even if null/empty (nullable fields)
    formData.append('email', data.email || '');
    formData.append('telefone', data.telefone || '');
    
    // Etiquetas - enviar cada etiqueta individualmente como valores separados
    if (data.etiquetas && Array.isArray(data.etiquetas) && data.etiquetas.length > 0) {
      data.etiquetas.forEach((etiqueta) => {
        formData.append('etiquetas', etiqueta);
      });
    }
    
    // enderecoStr - deve ser enviado como objeto JSON com os campos do endereço
    if (data.endereco) {
      const enderecoObj = {
        logradouro: data.endereco.logradouro,
        numero: data.endereco.numero,
        bairro: data.endereco.bairro,
        cidade: data.endereco.cidade,
        estado: data.endereco.estado,
        cep: data.endereco.cep,
        pais: data.endereco.pais,
        ibge: data.endereco.ibge || '',
      };
      formData.append('enderecoStr', JSON.stringify(enderecoObj));
    }

    const response = await this.urbNetDataSource.post<OuvidoriaCreationResponse>({
      endpoint: `v1/municipios/${municipioId}/ouvidorias`,
      body: formData,
      requiredToken: true,
      headers: { 'accept': 'application/vnd.data.full.v1+json' },
    });

    if ('failure' in response) {
      return response;
    }

    return { success: response.success.body as OuvidoriaCreationResponse };
  }

  /**
   * Creates a new user and extracts the tokens from the response
   */
  async criarUsuario(
    data: UserCreationData
  ): Promise<{ success: UserCreationResponse } | { failure: Failure }> {
    const response = await this.authDataSource.post<UserCreationResponse>({
      endpoint: 'autenticacao/v1/usuarios',
      body: data as unknown as Record<string, unknown>,
      requiredToken: false,
    });

    if ('failure' in response) {
      return response;
    }

    return { success: response.success.body as UserCreationResponse };
  }

  /**
   * Cria uma ouvidoria sem alterar a sess\u00e3o atual (AppConfig). Usado por um gestor
   * j\u00e1 autenticado para criar uma nova ouvidoria setorial supervisionada,
   * mantendo intactos `AppConfig.ouvidoriaId`, `municipioId`, `agenteId`, token, etc.
   *
   * Retorna o id da ouvidoria criada para que a UI o repasse adiante (cadastro
   * de agente etc.) sem precisar gravar no estado global.
   */
  async criarOuvidoriaParaSupervisao(
    municipioId: string,
    data: OuvidoriaCreationData
  ): Promise<{ success: { ouvidoriaId: string } } | { failure: Failure }> {
    // Use ouvidoria-geral endpoint if geral=true
    const isGeral = data.geral === true;
    const ouvidoriaResult = isGeral
      ? await this.criarOuvidoriaGeral(municipioId, data)
      : await this.criarOuvidoria(municipioId, data);

    if ('failure' in ouvidoriaResult) {
      return ouvidoriaResult;
    }
    return { success: { ouvidoriaId: ouvidoriaResult.success.id } };
  }

  /**
   * Creates a new ouvidoria and saves the ID to AppConfig and localStorage
   */
  async criarOuvidoriaESalvarId(
    municipioId: string,
    municipioLong: number,
    data: OuvidoriaCreationData
  ): Promise<{ success: { ouvidoriaId: string } } | { failure: Failure }> {
    // Step 1: Create ouvidoria
    // Use ouvidoria-geral endpoint if geral=true
    const isGeral = data.geral === true;
    const ouvidoriaResult = isGeral
      ? await this.criarOuvidoriaGeral(municipioId, data)
      : await this.criarOuvidoria(municipioId, data);

    if ('failure' in ouvidoriaResult) {
      return ouvidoriaResult;
    }

    const ouvidoriaResponse = ouvidoriaResult.success;

    // Extract ID from ouvidoria response
    const ouvidoriaId = ouvidoriaResponse.id;

    // Update AppConfig with ouvidoria data
    AppConfig.ouvidoriaId = ouvidoriaId;
    AppConfig.municipioId = municipioId;
    AppConfig.municipioLong = municipioLong;

    // Persist to localStorage (encrypted)
    try {
      const [ouvidoriaIdCifrado, municipioIdCifrado, municipioLongCifrado] = await Promise.all([
        encryptId(ouvidoriaId),
        encryptId(municipioId),
        encryptId(String(municipioLong)),
      ]);
      localStorage.setItem('ouvidoriaId', ouvidoriaIdCifrado);
      localStorage.setItem('municipioId', municipioIdCifrado);
      localStorage.setItem('municipioLong', municipioLongCifrado);
    } catch (error) {
      // Error saving to localStorage
    }

    return { success: { ouvidoriaId } };
  }

  /**
   * Main flow: Creates ouvidoria and user, then updates AppConfig with the returned data
   */
  async criarOuvidoriaEUsuario(
    municipioId: string,
    ouvidoriaData: OuvidoriaCreationData,
    userData: UserCreationData
  ): Promise<{ success: boolean } | { failure: Failure }> {
    // Step 1: Create ouvidoria
    // Use ouvidoria-geral endpoint if geral=true
    const isGeral = ouvidoriaData.geral === true;
    const ouvidoriaResult = isGeral
      ? await this.criarOuvidoriaGeral(municipioId, ouvidoriaData)
      : await this.criarOuvidoria(municipioId, ouvidoriaData);

    if ('failure' in ouvidoriaResult) {
      return ouvidoriaResult;
    }

    const ouvidoriaResponse = ouvidoriaResult.success;

    // Extract IDs from ouvidoria response
    const ouvidoriaId = ouvidoriaResponse.id;
    const agenteId = ouvidoriaResponse.agentes && ouvidoriaResponse.agentes.length > 0 
      ? ouvidoriaResponse.agentes[0].id 
      : '';

    // Update AppConfig with ouvidoria data
    AppConfig.ouvidoriaId = ouvidoriaId;
    AppConfig.municipioId = municipioId;
    AppConfig.agenteId = agenteId;
    // Note: municipioLong should be set separately as it comes from the municipio data

    // Step 2: Create user
    const userResult = await this.criarUsuario(userData);
    
    if ('failure' in userResult) {
      return userResult;
    }

    const userResponse = userResult.success;

    // Extract tokens from user response
    const tokenAcesso = userResponse.tokenAcesso;
    const refreshToken = userResponse.tokenAtualizacao;
    const dataExpiracao = userResponse.expira;
    const chaveRecuperacao = userResponse.recuperarConta?.chave;
    const segredoRecuperacao = userResponse.recuperarConta?.segredo;

    // Update AppConfig with token data
    AppConfig.tokenAcesso = tokenAcesso;
    AppConfig.refreshToken = refreshToken;
    AppConfig.dataExpiracao = dataExpiracao;
    AppConfig.chaveRecuperacao = chaveRecuperacao;
    AppConfig.segredoRecuperacao = segredoRecuperacao;
    AppConfig.token = { accessToken: tokenAcesso };

    // Step 3: Persist to localStorage (equivalent to SharedPreferences in Dart)
    await this.persistToLocalStorage({
      tokenAcesso,
      refreshToken,
      dataExpiracao,
      chaveRecuperacao,
      segredoRecuperacao,
      ouvidoriaId,
      municipioId,
      agenteId,
    });

    return { success: true };
  }

  /**
   * Persists configuration data to localStorage
   */
  private async persistToLocalStorage(data: {
    tokenAcesso: string;
    refreshToken: string;
    dataExpiracao: string;
    chaveRecuperacao?: string;
    segredoRecuperacao?: string;
    ouvidoriaId: string;
    municipioId: string;
    agenteId: string;
  }): Promise<void> {
    try {
      // Tokens encriptados (AES-GCM) antes de gravar em disco
      const [tokenAcessoCifrado, refreshTokenCifrado, ouvidoriaIdCifrado, municipioIdCifrado, agenteIdCifrado] = await Promise.all([
        encryptToken(data.tokenAcesso),
        encryptToken(data.refreshToken),
        encryptId(data.ouvidoriaId),
        encryptId(data.municipioId),
        encryptId(data.agenteId),
      ]);
      localStorage.setItem('tokenAcesso', tokenAcessoCifrado);
      localStorage.setItem('refreshToken', refreshTokenCifrado);
      localStorage.setItem('dataExpiracao', data.dataExpiracao);

      if (data.chaveRecuperacao) {
        localStorage.setItem('chaveRecuperacao', data.chaveRecuperacao);
      }

      if (data.segredoRecuperacao) {
        localStorage.setItem('segredoRecuperacao', data.segredoRecuperacao);
      }

      localStorage.setItem('ouvidoriaId', ouvidoriaIdCifrado);
      localStorage.setItem('municipioId', municipioIdCifrado);
      localStorage.setItem('agenteId', agenteIdCifrado);
    } catch (error) {
      // Error persisting to localStorage
    }
  }

  /**
   * Loads configuration data from localStorage
   */
  static async loadFromLocalStorage(): Promise<void> {
    try {
      const tokenAcessoCifrado = localStorage.getItem('tokenAcesso');
      const refreshTokenCifrado = localStorage.getItem('refreshToken');
      const dataExpiracao = localStorage.getItem('dataExpiracao');
      const chaveRecuperacao = localStorage.getItem('chaveRecuperacao');
      const segredoRecuperacao = localStorage.getItem('segredoRecuperacao');
      const ouvidoriaIdCifrado = localStorage.getItem('ouvidoriaId');
      const municipioIdCifrado = localStorage.getItem('municipioId');
      const agenteIdCifrado = localStorage.getItem('agenteId');

      // Tokens e IDs vêm encriptados do disco; decripta para uso em memória
      const [tokenAcesso, refreshToken, ouvidoriaId, municipioId, agenteId] = await Promise.all([
        tokenAcessoCifrado ? decryptToken(tokenAcessoCifrado) : Promise.resolve(''),
        refreshTokenCifrado ? decryptToken(refreshTokenCifrado) : Promise.resolve(''),
        ouvidoriaIdCifrado ? decryptId(ouvidoriaIdCifrado) : Promise.resolve(''),
        municipioIdCifrado ? decryptId(municipioIdCifrado) : Promise.resolve(''),
        agenteIdCifrado ? decryptId(agenteIdCifrado) : Promise.resolve(''),
      ]);

      if (tokenAcesso) AppConfig.tokenAcesso = tokenAcesso;
      if (refreshToken) AppConfig.refreshToken = refreshToken;
      if (dataExpiracao) AppConfig.dataExpiracao = dataExpiracao;
      if (chaveRecuperacao) AppConfig.chaveRecuperacao = chaveRecuperacao;
      if (segredoRecuperacao) AppConfig.segredoRecuperacao = segredoRecuperacao;
      if (ouvidoriaId) AppConfig.ouvidoriaId = ouvidoriaId;
      if (municipioId) AppConfig.municipioId = municipioId;
      if (agenteId) AppConfig.agenteId = agenteId;

      if (tokenAcesso) {
        AppConfig.token = { accessToken: tokenAcesso };
      }
    } catch (error) {
      // Error loading from localStorage
    }
  }
}

export { OuvidoriaCreationService };
export type { OuvidoriaCreationData, UserCreationData };
